import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, getDoc, where, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth, ROLES, PERMISSIONS } from '../../hooks/useAuth';
import OrderDetails from '../orders/OrderDetails';
import CustomerCard from './CustomerCard';
import { ORDER_STATUS, PAGINATION_OPTIONS, CURRENCY_SYMBOL } from '../../constants';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalizedConstants } from '../../i18n/useLocalizedConstants';
import { getSearchLocale } from '../../i18n/translateUtils';
import { formatDate, formatDateForInput, formatPrice, sanitizePhoneInput } from '../../utils';
import { calculateCustomerGrandTotal, calculateCustomerRemainingAmount } from '../../utils/customerFinancialUtils';
import { transferCommission, reverseCommissionFromUser, recalculateCommissionForUser, updateCommissionOrderDateForEntity } from '../../utils/commissionUtils';
import { isTulKanatProductType, restoreTulKanatStockForOrder, isQuantityStockProductType, restoreQuantityStockForOrder } from '../../utils/stockUtils';
import './CustomerList.css';

const CustomerList = ({ onCustomerChange, userRole, userId }) => {
  const { t } = useTranslation();
  const { ORDER_STATUS_LABELS } = useLocalizedConstants();
  const { user, permissions, isMontajci, isYazilimci, isAdmin } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    notes: '',
    inspectionDate: '',
    orderDate: '',
    deliveryDate: '',
    orderStatus: ORDER_STATUS.NEW_ORDER,
    paidAmount: '',
    isPaymentCompleted: false,
    remainingAmount: 0
  });
  const [isAdding, setIsAdding] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isCustomerInfoCollapsed, setIsCustomerInfoCollapsed] = useState(false); // Müşteri bilgileri için
  const [lastSubmitTime, setLastSubmitTime] = useState(0); // Çifte submit koruması için
  const isSubmittingRef = useRef(false); // Immediate blocking için

  // Aşamalı işlem için step state'i
  const [currentStep, setCurrentStep] = useState('customer'); // 'customer' veya 'order'

  // Filtreleme ve arama state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState('table'); // 'table' veya 'card'
  const [remainingAmounts, setRemainingAmounts] = useState({}); // Müşteri ID'lerine göre kalan ödeme
  const [selectedSalesUserId, setSelectedSalesUserId] = useState(null); // null = henüz seçilmedi (yeni müşteri)
  const [editingSalesUserId, setEditingSalesUserId] = useState(null); // Düzenleme modundaki müşterinin satışçısı

  // Toast notification gösterme fonksiyonu
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // Kalan ödeme hesaplama fonksiyonu
  const calculateRemainingAmount = useCallback(async (customer) => {
    try {
      const ordersSnapshot = await getDocs(query(
        collection(db, 'orders'),
        where('customerId', '==', customer.id)
      ));
      const orders = ordersSnapshot.docs.map(doc => doc.data());
      return calculateCustomerRemainingAmount(customer, orders);
    } catch (error) {
      console.error('Kalan ödeme hesaplanırken hata:', error);
      return 0;
    }
  }, []);

  // Sipariş / manuel toplam değişince müşteri finansal alanlarını ve anasayfayı senkronize et
  const syncCustomerFinancials = useCallback(async (customerId) => {
    if (!customerId || customerId.startsWith('temp_')) return;
    try {
      const customerSnap = await getDoc(doc(db, 'customers', customerId));
      if (!customerSnap.exists()) return;

      const customerData = { id: customerId, ...customerSnap.data() };
      const ordersSnapshot = await getDocs(query(
        collection(db, 'orders'),
        where('customerId', '==', customerId)
      ));
      const ordersData = ordersSnapshot.docs.map(d => d.data());

      const grandTotal = calculateCustomerGrandTotal(customerData, ordersData);
      const remaining = calculateCustomerRemainingAmount(customerData, ordersData);

      const updateData = {
        totalOrderAmount: grandTotal,
        remainingAmount: remaining,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'customers', customerId), updateData);

      setCustomers(prev =>
        prev.map(c => (c.id === customerId ? { ...c, ...updateData } : c))
      );
      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));
      setEditingCustomer(prev =>
        prev && prev.id === customerId ? { ...prev, ...updateData } : prev
      );

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.warn('Müşteri finansal bilgileri senkronize edilemedi:', error);
    }
  }, [onCustomerChange]);

  // Düzenleme kapanırken veya ödeme güncellenince kalan ödemeyi senkronize et
  const syncCustomerRemainingAmount = useCallback(async (customer) => {
    if (!customer?.id) return;
    try {
      const remaining = await calculateRemainingAmount(customer);
      setRemainingAmounts(prev => ({ ...prev, [customer.id]: remaining }));
      setCustomers(prev =>
        prev.map(c => (c.id === customer.id ? { ...c, remainingAmount: remaining } : c))
      );
      await updateDoc(doc(db, 'customers', customer.id), {
        remainingAmount: remaining,
        updatedAt: new Date()
      });
    } catch (error) {
      console.warn('Kalan ödeme güncellenirken hata:', error);
    }
  }, [calculateRemainingAmount]);

  // Müşterileri Firebase'den çek
  const fetchCustomers = useCallback(async () => {
    try {
      setIsLoading(true);

      let customersQuery;

      // Yazılımcı rolü: sadece kendi oluşturduğu müşterileri gör (ürün listesi ortak katalog)
      if (isYazilimci && userId) {
        customersQuery = query(
          collection(db, 'customers'),
          where('createdBy', '==', userId)
        );
      } else {
        // Admin, Satışcı: tüm müşterileri gör
        customersQuery = query(collection(db, 'customers'));
      }

      const querySnapshot = await getDocs(customersQuery);
      let customersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Eğer data içinde 'id' field'ı varsa temizle, document ID'yi kullan
        const { id: dataId, ...cleanData } = data;

        if (dataId && dataId !== doc.id) {
          console.warn('⚠️ Veri tutarsızlığı tespit edildi:', {
            documentId: doc.id,
            dataId: dataId,
            customerName: data.name
          });
        }

        return {
          id: doc.id, // Her zaman Firebase document ID'si kullan
          ...cleanData
        };
      });

      // Montajcı rolü: sadece sipariş durumu "Montaj" olan müşterileri gör
      if (isMontajci) {
        customersData = customersData.filter(c => c.orderStatus === ORDER_STATUS.MONTAJ);
      }

      // Client-side'da tarihe göre sırala (en yeni en üstte)
      customersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return dateB.getTime() - dateA.getTime(); // Descending order
      });

      setCustomers(customersData);

      // Kalan ödemeleri hesapla - önce müşteri kaydındaki remainingAmount'u kullan, yoksa hesapla
      const amounts = {};
      for (const customer of customersData) {
        // Müşteri kaydında zaten remainingAmount varsa onu kullan
        // Not: -1 değeri "sipariş yok" anlamına gelir, bu değeri de kabul et
        if (customer.remainingAmount !== undefined && customer.remainingAmount !== null && customer.remainingAmount !== 0) {
          amounts[customer.id] = parseFloat(customer.remainingAmount);
        } else if (customer.isPaymentCompleted) {
          // Ödeme tamamlandıysa 0
          amounts[customer.id] = 0;
        } else {
          // Yoksa veya 0 ise undefined olarak bırak, sonra lazy olarak hesaplanacak (performans için)
          // 0 değeri hem "ödeme alındı" hem "sipariş yok" olabilir, bu yüzden tekrar hesaplanmalı
          // undefined olursa "..." gösterilecek, hesaplanana kadar
          amounts[customer.id] = undefined;
        }
      }
      setRemainingAmounts(amounts);

      // Arka planda sadece remainingAmount olmayan veya 0 olan müşteriler için hesapla (lazy loading)
      // Bu işlemi async olarak yap, UI'ı bloklamasın
      // Hesaplanan değerleri Firebase'e kaydet ki bir sonraki yüklemede hızlı olsun
      // Not: remainingAmount 0 olanlar için de hesaplama yap çünkü "sipariş yok" (-1) olabilir
      setTimeout(async () => {
        const missingAmounts = {};
        const updatesToSave = [];

        for (const customer of customersData) {
          // remainingAmount undefined, null veya 0 ise hesapla
          // 0 değeri hem "ödeme alındı" hem "sipariş yok" olabilir
          const needsCalculation = customer.remainingAmount === undefined ||
            customer.remainingAmount === null ||
            customer.remainingAmount === 0;

          if (needsCalculation) {
            if (!customer.isPaymentCompleted) {
              const remaining = await calculateRemainingAmount(customer);
              missingAmounts[customer.id] = remaining;

              // Firebase'e kaydet (arka planda, hata olursa sessizce devam et)
              updatesToSave.push(
                updateDoc(doc(db, 'customers', customer.id), {
                  remainingAmount: remaining,
                  updatedAt: new Date()
                }).catch(err => {
                  console.warn(`Müşteri ${customer.id} için remainingAmount kaydedilemedi:`, err);
                })
              );
            } else {
              // Ödeme tamamlandıysa 0 olarak kaydet
              missingAmounts[customer.id] = 0;
              updatesToSave.push(
                updateDoc(doc(db, 'customers', customer.id), {
                  remainingAmount: 0,
                  updatedAt: new Date()
                }).catch(err => {
                  console.warn(`Müşteri ${customer.id} için remainingAmount kaydedilemedi:`, err);
                })
              );
            }
          }
        }

        // UI'ı güncelle
        if (Object.keys(missingAmounts).length > 0) {
          setRemainingAmounts(prev => ({ ...prev, ...missingAmounts }));
        }

        // Firebase'e kaydet (tüm güncellemeleri paralel yap)
        if (updatesToSave.length > 0) {
          Promise.all(updatesToSave).then(() => {
            console.log(`✅ ${updatesToSave.length} müşteri için remainingAmount Firebase'e kaydedildi`);
          }).catch(err => {
            console.warn('Bazı remainingAmount güncellemeleri başarısız oldu:', err);
          });
        }
      }, 100);

      // Dashboard istatistiklerini güncelle
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      showNotification('Müşteriler yüklenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [onCustomerChange, calculateRemainingAmount, isYazilimci, isMontajci, userId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Müşterileri filtrele
  useEffect(() => {
    let filtered = customers;

    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLocaleLowerCase(getSearchLocale());
      filtered = filtered.filter(customer =>
        customer.name?.toLocaleLowerCase(getSearchLocale()).includes(searchLower) ||
        customer.phone1?.includes(searchTerm) ||
        customer.address?.toLocaleLowerCase(getSearchLocale()).includes(searchLower)
      );
    }

    // Durum filtresi
    if (statusFilter) {
      filtered = filtered.filter(customer => customer.orderStatus === statusFilter);
    }

    setFilteredCustomers(filtered);
    setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
  }, [customers, searchTerm, statusFilter]);

  // Sayfalama hesaplamaları (memoize - filteredCustomers/currentPage/itemsPerPage değişmedikçe yeniden hesaplanmaz)
  const totalPages = useMemo(
    () => Math.ceil(filteredCustomers.length / itemsPerPage),
    [filteredCustomers.length, itemsPerPage]
  );
  const currentCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCustomers, currentPage, itemsPerPage]);

  // Sayfa değiştirme
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Sayfa başına öğe sayısını değiştirme
  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  }, []);

  // Yeni müşteri ekle
  const handleAddCustomer = async (e) => {
    e.preventDefault();

    // IMMEDIATE BLOCKING - Hiçbir koşulda geçilmez
    if (isSubmittingRef.current) {
      console.log('🚫 IMMEDIATE BLOCK - Already submitting');
      return;
    }

    // Çifte submit koruması - 2 saniye içinde ikinci submit'i engelle
    const now = Date.now();
    if (now - lastSubmitTime < 2000) {
      console.log('⚠️ Çifte submit engellendi:', {
        timeDiff: now - lastSubmitTime,
        lastSubmit: new Date(lastSubmitTime).toLocaleTimeString()
      });
      showNotification(t('customers.notifications.waitInProgress'), 'warning');
      return;
    }

    // Zaten işlem devam ediyorsa engelleyelin
    if (isLoading) {
      console.log('⚠️ Loading state aktif, submit engellendi');
      return;
    }

    if (!newCustomer.name || !newCustomer.phone1) {
      showNotification(t('customers.notifications.requiredFields'), 'warning');
      return;
    }

    try {
      // İşlem başladığını hemen işaretle
      isSubmittingRef.current = true;
      setLastSubmitTime(now);
      setIsLoading(true);

      console.log('📝 Müşteri ekleme başladı:', new Date().toLocaleTimeString());

      const customerData = {
        name: newCustomer.name,
        phone1: newCustomer.phone1,
        phone2: newCustomer.phone2 || '',
        email: newCustomer.email || '',
        address: newCustomer.address || '',
        notes: newCustomer.notes || '',
        inspectionDate: newCustomer.inspectionDate || '',
        orderDate: newCustomer.orderDate || '',
        deliveryDate: newCustomer.deliveryDate || '',
        orderStatus: newCustomer.orderStatus,
        paidAmount: newCustomer.paidAmount || '',
        isPaymentCompleted: newCustomer.isPaymentCompleted || false,
        remainingAmount: newCustomer.remainingAmount || 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      // Müşteriyi Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), customerData);

      console.log('✅ Müşteri Firebase\'e kaydedildi:', docRef.id);

      // Kaydedilen müşterinin ID'sini al
      const savedCustomer = { ...newCustomer, id: docRef.id };

      // Sipariş aşamasına geç
      setCurrentStep('order');
      setNewCustomer(savedCustomer);

      // Local state'i güncelle (okuma maliyetini sıfırlamak için)
      setCustomers(prev => [{ id: docRef.id, ...customerData }, ...prev]);
      setRemainingAmounts(prev => ({ ...prev, [docRef.id]: -1 }));
      if (onCustomerChange) {
        onCustomerChange();
      }

      // Ekranı yukarı scroll et
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      showNotification(t('customers.notifications.customerSaved'), 'success');
    } catch (error) {
      console.error('❌ Müşteri eklenirken hata:', error);
      showNotification(t('customers.notifications.customerSaveError', { message: error.message }), 'error');
    } finally {
      // İşlem bitti, blokları kaldır
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Müşteri ekleme işlemini tamamla
  const handleCompleteCustomer = async () => {
    try {
      // Müşteri bilgilerini güncelle (sipariş durumu dahil)
      if (newCustomer.id && !newCustomer.id.startsWith('temp_')) {
        console.log('🔄 Müşteri güncelleniyor:', {
          customerId: newCustomer.id,
          orderStatus: newCustomer.orderStatus
        });

        // Direkt Firebase'den müşteriyi kontrol et (state timing sorunu için)
        try {
          const customerDoc = await getDoc(doc(db, 'customers', newCustomer.id));

          if (!customerDoc.exists()) {
            console.error('❌ Müşteri Firebase\'de bulunamadı:', newCustomer.id);
            showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
            await fetchCustomers();
            setIsAdding(false);
            setCurrentStep('customer');
            return;
          }

          console.log('✅ Müşteri Firebase\'de mevcut:', newCustomer.id);
        } catch (checkError) {
          console.error('❌ Müşteri kontrol hatası:', checkError);
          showNotification('Müşteri kontrolü yapılamadı: ' + checkError.message, 'error');
          return;
        }

        // Ödeme bilgilerini de kaydet
        const paidAmountValue = (newCustomer.paidAmount && newCustomer.paidAmount.toString().trim() !== '')
          ? parseFloat(newCustomer.paidAmount)
          : 0;

        // Siparişleri kontrol et - sipariş yoksa remainingAmount -1 olmalı
        const ordersQuery = query(
          collection(db, 'orders'),
          where('customerId', '==', newCustomer.id)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        const hasOrders = ordersSnapshot.docs.length > 0;

        // remainingAmount hesapla: sipariş yoksa -1, varsa newCustomer'dan al
        let remainingAmountValue;
        if (!hasOrders) {
          remainingAmountValue = -1; // Sipariş yok
        } else if (newCustomer.isPaymentCompleted) {
          remainingAmountValue = 0; // Ödeme tamamlandı
        } else {
          remainingAmountValue = newCustomer.remainingAmount || 0;
        }

        const updateData = {
          orderStatus: newCustomer.orderStatus,
          genelIskontoYuzde: newCustomer.genelIskontoYuzde || '',
          paidAmount: paidAmountValue,
          isPaymentCompleted: newCustomer.isPaymentCompleted || false,
          remainingAmount: remainingAmountValue,
          salesUserId: selectedSalesUserId || null,
          updatedAt: new Date()
        };

        await updateDoc(doc(db, 'customers', newCustomer.id), updateData);

        // Local state'i güncelle (okuma maliyetini sıfırlamak için)
        setCustomers(prev =>
          prev.map(c => c.id === newCustomer.id ? { ...c, ...updateData } : c)
        );
        setRemainingAmounts(prev => ({
          ...prev,
          [newCustomer.id]: remainingAmountValue
        }));

        if (onCustomerChange) {
          onCustomerChange();
        }

        console.log('✅ Müşteri güncellendi:', newCustomer.id);
      } else {
        console.warn('⚠️ Geçersiz customer ID, güncelleme atlanıyor:', newCustomer.id);
      }

      // Prim uygula (satışçı seçilmişse ve "seçim yapmak istemiyorum" değilse)
      if (
        selectedSalesUserId &&
        selectedSalesUserId !== 'no-selection' &&
        newCustomer.id &&
        !newCustomer.id.startsWith('temp_')
      ) {
        try {
          const ordersSnap = await getDocs(query(
            collection(db, 'orders'),
            where('customerId', '==', newCustomer.id)
          ));
          const allOrders = ordersSnap.docs.map(d => d.data());
          const grandTotal = calculateCustomerGrandTotal(newCustomer, allOrders);
          await recalculateCommissionForUser(
            db,
            selectedSalesUserId,
            newCustomer.id,
            newCustomer.name || '',
            grandTotal,
            false,
            newCustomer.orderDate
          );
        } catch (commErr) {
          console.error('Prim uygulanırken hata:', commErr);
        }
      }

      // Formu temizle
      setNewCustomer({
        name: '',
        phone1: '',
        phone2: '',
        email: '',
        address: '',
        notes: '',
        inspectionDate: '',
        orderDate: '',
        deliveryDate: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        genelIskontoYuzde: '',
        paidAmount: '',
        isPaymentCompleted: false,
        remainingAmount: 0
      });
      setIsAdding(false);
      setCurrentStep('customer');
      setSelectedSalesUserId(null);

      showNotification(t('customers.notifications.customerCompleted'), 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification(t('customers.notifications.customerUpdateError', { message: error.message }), 'error');
    }
  };

  // Sipariş durumu değişikliği için callback
  const handleNewCustomerOrderStatusChange = useCallback((status) => {
    setNewCustomer(prev => ({ ...prev, orderStatus: status }));
  }, []);

  // Genel iskonto yüzde değişikliği için callback (yeni müşteri)
  const handleNewCustomerGenelIskontoYuzdeChange = useCallback((genelIskontoYuzde) => {
    setNewCustomer(prev => ({ ...prev, genelIskontoYuzde: genelIskontoYuzde }));
  }, []);

  // Düzenleme modunda fiyat (vade/iskonto) kaydetme başarılı olduğunda
  const handleEditCustomerVadeliSaveSuccess = useCallback(async (pricingData) => {
    const { genelIskontoYuzde } = pricingData;

    // editingCustomer state'ini güncelle (anlık görüntü için)
    if (editingCustomer) {
      const customerId = editingCustomer.id;
      const updatedCustomer = { ...editingCustomer, genelIskontoYuzde };
      setEditingCustomer(updatedCustomer);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? updatedCustomer
            : customer
        )
      );

      // Beklenen gelir ve kalan ödemeyi senkronize et
      await syncCustomerFinancials(customerId);
    }

    showNotification(t('customers.notifications.pricingUpdated'), 'success');
  }, [editingCustomer, syncCustomerFinancials]);

  // Ödeme bilgisi güncellendiğinde sadece ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
  const handleCustomerPaymentUpdate = useCallback((updatedCustomerInfo) => {
    const customerId = updatedCustomerInfo.id;
    if (!customerId) return;

    const remaining = updatedCustomerInfo.remainingAmount ?? 0;

    // editingCustomer state'ini güncelle
    setEditingCustomer(prev =>
      prev && prev.id === customerId ? { ...prev, ...updatedCustomerInfo } : prev
    );

    // Müşteri listesindeki ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === customerId
          ? { ...customer, ...updatedCustomerInfo, remainingAmount: remaining }
          : customer
      )
    );

    // Kalan ödeme state'ini anında güncelle
    setRemainingAmounts(prev => ({
      ...prev,
      [customerId]: remaining
    }));

    if (updatedCustomerInfo.updateType === 'manualTotalSave') {
      showNotification('Genel toplam güncellendi.', 'success');
    } else if (updatedCustomerInfo.updateType === 'manualTotalRevert') {
      showNotification('Otomatik hesaplamaya döndürüldü.', 'success');
    } else {
      showNotification(t('customers.notifications.paymentUpdated'), 'success');
    }

    // Anasayfa istatistiklerini yenile
    if (onCustomerChange) {
      onCustomerChange();
    }
  }, [onCustomerChange]);

  // Sipariş durumu değiştiğinde müşteri listesini güncelle
  const handleOrderStatusChange = async (customerId, newStatus) => {
    // Customer ID geçerliliğini kontrol et
    if (!customerId || customerId.startsWith('temp_')) {
      showNotification('Geçersiz müşteri ID\'si. Sayfayı yenileyip tekrar deneyin.', 'error');
      return;
    }

    try {
      // Önce müşterinin var olup olmadığını kontrol et
      const customerExists = customers.find(c => c.id === customerId);

      if (!customerExists) {
        showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
        await fetchCustomers(); // Listeyi yenile
        return;
      }

      await updateDoc(doc(db, 'customers', customerId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });

      // editingCustomer state'ini güncelle
      if (editingCustomer && editingCustomer.id === customerId) {
        setEditingCustomer({
          ...editingCustomer,
          orderStatus: newStatus
        });
      }

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, orderStatus: newStatus }
            : customer
        )
      );

      showNotification(t('customers.notifications.orderStatusUpdated'), 'success');
    } catch (error) {
      console.error('Sipariş durumu güncellenirken hata:', error);

      // Eğer döküman bulunamadı hatası ise, UI'ı temizle
      if (error.code === 'not-found') {
        // Edit modunu kapat
        if (editingCustomer && editingCustomer.id === customerId) {
          setEditingCustomer(null);
          document.body.classList.remove('modal-open');
        }

        // Listeyi yenile
        await fetchCustomers();

        showNotification('Bu müşteri artık mevcut değil. Liste güncellendi.', 'warning');
      } else {
        showNotification('Sipariş durumu güncellenirken bir hata oluştu: ' + error.message, 'error');

        // Hata durumunda listeyi yenile
        await fetchCustomers();
      }
    }
  };

  // Müşteri kopyala (temel bilgileri alır, tarihler ve siparişler hariç)
  const handleCopyCustomer = async (customer) => {
    try {
      setIsLoading(true);

      // Sadece temel müşteri bilgilerini al (not, tarihler ve siparişler hariç)
      const copiedCustomerData = {
        name: customer.name + ' (Kopya)',
        phone1: customer.phone1 || '',
        phone2: customer.phone2 || '',
        email: customer.email || '',
        address: customer.address || '',
        notes: '', // Not kopyalanmaz
        inspectionDate: '', // Keşif tarihi kopyalanmaz
        orderDate: '', // Sipariş tarihi kopyalanmaz
        deliveryDate: '', // Teslimat tarihi kopyalanmaz
        orderStatus: ORDER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: -1, // Sipariş yok
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null // Hangi kullanıcı oluşturduğu
      };

      // Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), copiedCustomerData);

      // Local state'e ekle (tüm listeyi yenilemek yerine)
      const newCustomer = {
        id: docRef.id,
        ...copiedCustomerData
      };

      setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);
      setRemainingAmounts(prev => ({ ...prev, [docRef.id]: -1 }));

      showNotification(`"${customer.name}" müşterisi kopyalandı!`, 'success');
    } catch (error) {
      console.error('Müşteri kopyalanırken hata:', error);
      showNotification('Müşteri kopyalanırken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri silme onayı
  const handleDeleteConfirm = (customer) => {
    setDeleteConfirm(customer);
  };

  // Müşteri sil
  const handleDeleteCustomer = async () => {
    if (!deleteConfirm) return;

    try {
      setIsLoading(true);

      const deletedId = deleteConfirm.id;
      const salesUserId = deleteConfirm.salesUserId;

      // 1) Müşteriye ait tüm siparişleri çek
      const ordersSnap = await getDocs(query(
        collection(db, 'orders'),
        where('customerId', '==', deletedId)
      ));
      const ordersData = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // 2) Tül/Kanat ve Rustik/Aksesuar siparişleri için stok geri yükle
      for (const order of ordersData) {
        try {
          if (isTulKanatProductType(order.productType)) {
            await restoreTulKanatStockForOrder(db, order, false); // perakende
          } else if (isQuantityStockProductType(order.productType)) {
            await restoreQuantityStockForOrder(db, order);
          }
        } catch (stockErr) {
          console.error('Stok geri yükleme hatası:', stockErr);
        }
      }

      // 3) Tüm siparişleri sil
      await Promise.all(ordersSnap.docs.map(d => deleteDoc(doc(db, 'orders', d.id))));

      // 4) Müşteri dokümanını sil
      await deleteDoc(doc(db, 'customers', deletedId));

      // 5) Primleri satışçıdan geri al
      if (salesUserId && salesUserId !== 'no-selection') {
        try {
          await reverseCommissionFromUser(db, salesUserId, deletedId);
        } catch (commErr) {
          console.error('Müşteri silinirken prim iptali hatası:', commErr);
        }
      }

      setDeleteConfirm(null);

      // Local state'i güncelle
      setCustomers(prevCustomers => prevCustomers.filter(customer => customer.id !== deletedId));
      setRemainingAmounts(prev => {
        const newAmounts = { ...prev };
        delete newAmounts[deletedId];
        return newAmounts;
      });

      showNotification(t('customers.notifications.customerDeleted'), 'success');
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      showNotification(t('customers.notifications.customerDeleteError', { message: error.message }), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri düzenleme modunu aç
  const handleEditCustomer = (customer) => {
    // Tarihleri input için uygun formata çevir
    const customerForEdit = {
      ...customer,
      inspectionDate: formatDateForInput(customer.inspectionDate),
      orderDate: formatDateForInput(customer.orderDate),
      deliveryDate: formatDateForInput(customer.deliveryDate)
    };

    setEditingCustomer(customerForEdit);
    setEditingSalesUserId(customer.salesUserId || null);

    // Modal açıldığında body scroll'u engelle
    document.body.classList.add('modal-open');

    // Edit modu açıldığında otomatik scroll
    setTimeout(() => {
      const editSection = document.querySelector('.edit-customer-section');
      if (editSection) {
        editSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Müşteri güncelle
  const handleUpdateCustomer = async (e) => {
    if (e) e.preventDefault();

    if (!editingCustomer.name || !editingCustomer.phone1) {
      showNotification(t('customers.notifications.requiredFields'), 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const customerId = editingCustomer.id;

      const updatedData = {
        name: editingCustomer.name,
        phone1: editingCustomer.phone1,
        phone2: editingCustomer.phone2,
        email: editingCustomer.email,
        address: editingCustomer.address,
        notes: editingCustomer.notes,
        inspectionDate: editingCustomer.inspectionDate,
        orderDate: editingCustomer.orderDate,
        deliveryDate: editingCustomer.deliveryDate,
        tcNo: deleteField(),
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'customers', customerId), updatedData);

      const { tcNo: _removedTcNo, ...localUpdatedData } = updatedData;

      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? (() => {
                const { tcNo: _oldTcNo, ...rest } = customer;
                return { ...rest, ...localUpdatedData };
              })()
            : customer
        )
      );

      // Kalan ödemeyi hesapla ve güncelle
      const remaining = await calculateRemainingAmount(editingCustomer);
      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));

      // Firebase'e remainingAmount kaydet
      await updateDoc(doc(db, 'customers', customerId), {
        remainingAmount: remaining
      });

      const salesUserId = editingSalesUserId || editingCustomer.salesUserId;
      if (salesUserId && salesUserId !== 'no-selection' && editingCustomer.orderDate) {
        try {
          await updateCommissionOrderDateForEntity(
            db,
            salesUserId,
            customerId,
            editingCustomer.orderDate
          );
        } catch (commErr) {
          console.error('Sipariş tarihi güncellenirken prim tarihi hatası:', commErr);
        }
      }

      setEditingCustomer(null);
      setEditingSalesUserId(null);
      // Modal kapandığında body scroll'u geri aç
      document.body.classList.remove('modal-open');

      showNotification(t('customers.notifications.customerUpdated'), 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification('Müşteri güncellenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="customer-list-container">
      {/* Toast Notification */}
      {notification && (
        <div className={`toast-notification ${notification.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {notification.type === 'success' && <i className="fas fa-check-circle"></i>}
              {notification.type === 'error' && <i className="fas fa-exclamation-circle"></i>}
              {notification.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
            </div>
            <div className="toast-message">{notification.message}</div>
            <button
              className="toast-close"
              onClick={() => setNotification(null)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      <div className="customer-header">
        <h2>{isMontajci ? t('dashboard.nav.installationList') : t('dashboard.nav.customerList')}</h2>
        {permissions?.canCreateCustomer && (
          <button
            onClick={() => setIsAdding(!isAdding)}
            className="add-customer-btn"
            disabled={isLoading}
          >
            {isAdding ? t('common.cancel') : `+ ${t('customers.newCustomer')}`}
          </button>
        )}
      </div>

      {/* Filtreleme ve Arama */}
      {!isAdding && !editingCustomer && (
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>{t('customers.search')}</label>
              <input
                type="text"
                placeholder={t('customers.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <label>{t('customers.orderStatus')}</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter"
              >
                <option value="">{t('common.all')}</option>
                {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tablo Üstü Kontroller */}
      {!isAdding && !editingCustomer && (
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="filter-group">
              <label>{t('customers.itemsPerPage')}</label>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="items-per-page"
              >
                {PAGINATION_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title={t('customers.viewTable')}
              >
                <i className="fas fa-table"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => setViewMode('card')}
                title={t('customers.viewCard')}
              >
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdding && (
        <div className="add-customer-form">
          <h3>{t('customers.form.addTitle')}</h3>

          <div className="step-indicator">
            <div
              className={`step ${currentStep === 'customer' ? 'active' : 'completed'}`}
              onClick={() => setCurrentStep('customer')}
              style={{ cursor: 'pointer' }}
            >
              <span className="step-number">1</span>
              <span className="step-text">{t('customers.form.stepCustomer')}</span>
            </div>
            <div
              className={`step ${currentStep === 'order' ? 'active' : ''}`}
              onClick={() => {
                if (newCustomer.id) {
                  setCurrentStep('order');
                }
              }}
              style={{ cursor: newCustomer.id ? 'pointer' : 'not-allowed' }}
            >
              <span className="step-number">2</span>
              <span className="step-text">{t('customers.form.stepOrder')}</span>
            </div>
          </div>

          <div className={`customer-info-section ${currentStep === 'customer' ? 'active' : 'hidden'}`}>
            <h4>{t('customers.form.customerInfo')}</h4>
            <form onSubmit={handleAddCustomer}>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('customers.form.fullName')} *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('customers.form.phone1')} *</label>
                  <input
                    type="tel"
                    value={newCustomer.phone1}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone1: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('customers.form.phonePlaceholder')}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('customers.form.phone2')}</label>
                  <input
                    type="tel"
                    value={newCustomer.phone2}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone2: sanitizePhoneInput(e.target.value) })}
                    placeholder={t('customers.form.phonePlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('customers.form.email')}</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>{t('customers.form.address')}</label>
                <textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group full-width">
                <label>{t('customers.form.notes')}</label>
                <textarea
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-grid date-grid">
                <div className="form-group">
                  <label>{t('customers.form.inspectionDate')}</label>
                  <input
                    type="date"
                    value={newCustomer.inspectionDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, inspectionDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>{t('customers.form.orderDate')}</label>
                  <input
                    type="date"
                    value={newCustomer.orderDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, orderDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>{t('customers.form.deliveryDate')}</label>
                  <input
                    type="date"
                    value={newCustomer.deliveryDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, deliveryDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`save-btn ${(isLoading || isSubmittingRef.current) ? 'loading' : ''}`}
                  disabled={isLoading || isSubmittingRef.current}
                  style={{
                    pointerEvents: (isLoading || isSubmittingRef.current) ? 'none' : 'auto',
                    userSelect: 'none',
                    opacity: (isLoading || isSubmittingRef.current) ? 0.6 : 1
                  }}
                >
                  {(isLoading || isSubmittingRef.current) ? t('customers.form.saving') : t('customers.form.saveCustomer')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAdding(false);
                    setCurrentStep('customer');
                  }}
                  className="cancel-btn"
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>

          {/* Alt Kısım - Sipariş Detayları */}
          <div className={`order-details-section ${currentStep === 'order' ? 'active' : 'hidden'}`}>
            <OrderDetails
              customerId={newCustomer.id || 'temp_' + Date.now()}
              customerName={newCustomer.name}
              customerInfo={newCustomer}
              isLoading={isLoading}
              orderStatus={newCustomer.orderStatus}
              onOrderStatusChange={handleNewCustomerOrderStatusChange}
              onGenelIskontoYuzdeChange={handleNewCustomerGenelIskontoYuzdeChange}
              isNewCustomerWizard
              permissions={permissions}
              selectedSalesUserId={selectedSalesUserId}
              onSalesUserChange={(userId) => setSelectedSalesUserId(userId)}
              onCustomerSaved={(paymentInfo) => {
                // Ödeme bilgileri değiştiğinde newCustomer state'ini güncelle
                if (paymentInfo && typeof paymentInfo === 'object' && 'paidAmount' in paymentInfo) {
                  setNewCustomer(prev => ({
                    ...prev,
                    paidAmount: paymentInfo.paidAmount,
                    isPaymentCompleted: paymentInfo.isPaymentCompleted,
                    remainingAmount: paymentInfo.remainingAmount
                  }));
                }
              }}
            />

            {/* İşlemi Tamamla butonu sipariş detaylarının altında */}
            <div className="complete-form-actions">
              <button
                type="button"
                onClick={handleCompleteCustomer}
                className="complete-btn"
                disabled={isLoading || selectedSalesUserId === null}
                title={selectedSalesUserId === null ? t('orders.details.salesperson.required') : ''}
              >
                {t('customers.form.complete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCustomer && (
        <div className="edit-customer-section">
          <div className="edit-customer-header">
            <div className="header-left">
              <h3>{t('customers.form.detailsTitle')}</h3>
              <span className="auto-save-info">
                <i className="fas fa-info-circle"></i>
                {t('customers.form.autoSaveInfo')}
              </span>
            </div>
            <button
              onClick={() => {
                const customerSnapshot = editingCustomer;
                setEditingCustomer(null);
                setEditingSalesUserId(null);
                document.body.classList.remove('modal-open');
                syncCustomerRemainingAmount(customerSnapshot);
              }}
              className="close-edit-btn"
              title="Kapat"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="edit-customer-content">
            {/* Müşteri Bilgileri Bölümü */}
            <div className="customer-info-section">
              <div
                className="section-header collapsible"
                onClick={() => setIsCustomerInfoCollapsed(!isCustomerInfoCollapsed)}
              >
                <h4>{t('customers.form.customerInfo')}</h4>
                <i className={`fas fa-chevron-${isCustomerInfoCollapsed ? 'down' : 'up'}`}></i>
              </div>

              <div className={`section-content ${isCustomerInfoCollapsed ? 'collapsed' : ''}`}>
                <form onSubmit={handleUpdateCustomer}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('customers.form.fullName')} *</label>
                      <input
                        type="text"
                        value={editingCustomer.name}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('customers.form.phone1')} *</label>
                      <input
                        type="tel"
                        value={editingCustomer.phone1 || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, phone1: sanitizePhoneInput(e.target.value) })}
                        placeholder={t('customers.form.phonePlaceholder')}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('customers.form.phone2')}</label>
                      <input
                        type="tel"
                        value={editingCustomer.phone2 || ''}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, phone2: sanitizePhoneInput(e.target.value) })}
                        placeholder={t('customers.form.phonePlaceholder')}
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('customers.form.email')}</label>
                      <input
                        type="email"
                        value={editingCustomer.email}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group full-width" style={{ marginTop: '10px' }}>
                    <label>{t('customers.form.address')}</label>
                    <textarea
                      value={editingCustomer.address}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>{t('customers.form.notes')}</label>
                    <textarea
                      value={editingCustomer.notes}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, notes: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-grid" style={{ marginTop: '10px' }}>
                    <div className="form-group">
                      <label>{t('customers.form.inspectionDate')}</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.inspectionDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, inspectionDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('customers.form.orderDate')}</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.orderDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, orderDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('customers.form.deliveryDate')}</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.deliveryDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, deliveryDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="save-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? t('customers.form.updating') : t('customers.form.update')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const customerSnapshot = editingCustomer;
                        setEditingCustomer(null);
                        setEditingSalesUserId(null);
                        document.body.classList.remove('modal-open');
                        syncCustomerRemainingAmount(customerSnapshot);
                      }}
                      className="cancel-btn"
                      disabled={isLoading}
                    >
                      {t('customers.form.close')}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sipariş Detayları Bölümü */}
            <div className="order-details-section">
              <OrderDetails
                customerId={editingCustomer.id}
                customerName={editingCustomer.name}
                customerInfo={editingCustomer}
                isLoading={isLoading}
                orderStatus={editingCustomer.orderStatus}
                onOrderStatusChange={(status) => handleOrderStatusChange(editingCustomer.id, status)}
                permissions={permissions}
                onSaveVadeliSuccess={handleEditCustomerVadeliSaveSuccess}
                onCustomerSaved={handleCustomerPaymentUpdate}
                onOrderSaved={() => syncCustomerFinancials(editingCustomer.id)}
                selectedSalesUserId={editingSalesUserId}
                onSalesUserChange={isAdmin ? async (newUserId) => {
                  const oldUserId = editingSalesUserId;
                  setEditingSalesUserId(newUserId);
                  try {
                    await updateDoc(doc(db, 'customers', editingCustomer.id), {
                      salesUserId: newUserId || null,
                      updatedAt: new Date()
                    });
                    setCustomers(prev => prev.map(c =>
                      c.id === editingCustomer.id ? { ...c, salesUserId: newUserId || null } : c
                    ));
                    if (newUserId !== oldUserId) {
                      const ordersSnap = await getDocs(query(
                        collection(db, 'orders'),
                        where('customerId', '==', editingCustomer.id)
                      ));
                      const allOrders = ordersSnap.docs.map(d => d.data());
                      const grandTotal = calculateCustomerGrandTotal(editingCustomer, allOrders);
                      await transferCommission(
                        db, oldUserId, newUserId,
                        editingCustomer.id, editingCustomer.name,
                        grandTotal, false,
                        editingCustomer.orderDate
                      );
                    }
                  } catch (err) {
                    console.error('Satışçı güncelleme hatası:', err);
                  }
                } : undefined}
              />
            </div>
          </div>
        </div>
      )}

      <div className="customers-table">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>{t('customers.loading')}</p>
          </div>
        )}

        {viewMode === 'table' ? (
          // Tablo Görünümü
          <table>
            <thead>
              <tr>
                <th>{t('customers.table.name')}</th>
                <th>{t('customers.table.phone1')}</th>
                <th>{t('customers.table.orderDate')}</th>
                <th>{t('customers.table.deliveryDate')}</th>
                <th>{t('customers.table.orderStatus')}</th>
                <th>{t('customers.table.address')}</th>
                <th>{t('customers.table.remainingPayment')}</th>
                <th>{t('customers.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.name}</td>
                  <td>{customer.phone1}</td>
                  <td>{formatDate(customer.orderDate)}</td>
                  <td>{formatDate(customer.deliveryDate)}</td>
                  <td>
                    <span className={`status-badge status-${customer.orderStatus || ORDER_STATUS.NEW_ORDER}`}>
                      {ORDER_STATUS_LABELS[customer.orderStatus || ORDER_STATUS.NEW_ORDER]}
                    </span>
                  </td>
                  <td>{customer.address}</td>
                  <td>
                    {remainingAmounts[customer.id] !== undefined ? (
                      remainingAmounts[customer.id] === -1 ? (
                        <span className="payment-badge payment-no-order-badge">
                          <i className="fas fa-minus-circle"></i>
                          <span>{t('customers.table.noOrder')}</span>
                        </span>
                      ) : (customer.isPaymentCompleted || remainingAmounts[customer.id] === 0) ? (
                        <span className="payment-badge payment-completed-badge">
                          <i className="fas fa-check-circle"></i>
                          <span>{t('customers.table.paymentReceived')}</span>
                        </span>
                      ) : (
                        <span className="payment-badge payment-pending-badge">
                          <i className="fas fa-exclamation-circle"></i>
                          <span>{formatPrice(remainingAmounts[customer.id])} {CURRENCY_SYMBOL}</span>
                        </span>
                      )
                    ) : (
                      <span className="payment-badge payment-loading-badge">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>{t('customers.table.calculating')}</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {/* Düzenleme/Görüntüleme butonu */}
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="edit-btn"
                        disabled={isLoading}
                        title={permissions?.canEditCustomer ? t('common.edit') : t('customers.details')}
                      >
                        <i className={`fas ${permissions?.canEditCustomer ? 'fa-edit' : 'fa-eye'}`}></i>
                      </button>

                      {/* Kopyalama butonu - Montajcı göremez */}
                      {permissions?.canCreateCustomer && (
                        <button
                          onClick={() => handleCopyCustomer(customer)}
                          className="copy-btn"
                          disabled={isLoading}
                          title={t('customers.copyCustomer')}
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      )}

                      {/* Silme butonu - Montajcı göremez */}
                      {permissions?.canDeleteCustomer && (
                        <button
                          onClick={() => handleDeleteConfirm(customer)}
                          className="delete-btn"
                          disabled={isLoading}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // Kart Görünümü
          <div className="customers-grid">
            {currentCustomers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onEdit={handleEditCustomer}
                onCopy={handleCopyCustomer}
                onDelete={handleDeleteConfirm}
                isLoading={isLoading}
                formatDate={formatDate}
                remainingAmount={remainingAmounts[customer.id]}
                permissions={permissions}
              />
            ))}
          </div>
        )}

        {currentCustomers.length === 0 && !isLoading && (
          <div className="no-customers">
            <p>{t('customers.empty')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredCustomers.length > 0 && (
        <div className="pagination-section">
          <div className="pagination-info">
            <span>
              {t('customers.pagination', {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filteredCustomers.length),
                total: filteredCustomers.length
              })}
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              if (page > totalPages) return null;

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('customers.deleteModal.title')}</h3>
            <p>
              <Trans
                i18nKey="customers.deleteModal.confirm"
                values={{ name: deleteConfirm.name }}
                components={{ strong: <strong /> }}
              />
            </p>
            <p className="warning-text">{t('common.cannotUndo')}</p>
            <div className="modal-actions">
              <button
                onClick={handleDeleteCustomer}
                className="delete-confirm-btn"
                disabled={isLoading}
              >
                {isLoading ? t('common.deleting') : t('common.yesDelete')}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="cancel-btn"
                disabled={isLoading}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList; 